import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackFunnelEvent } from '../utils/trackFunnelEvent';

export interface CartItem {
  menu_item_id: string;
  name: string;
  price: number;
  quantity: number;
  category?: string;
  note?: string;
  // Starbucks-style drink options (size, milk, sweetness, etc.)
  customizations?: Record<string, string>;
}

interface CartContextType {
  items: CartItem[];
  cafeId: string | null;
  addItem: (item: CartItem, cafeId: string) => void;
  removeItem: (menuItemId: string) => void;
  updateQuantity: (menuItemId: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function readSavedCart(): { items: CartItem[]; cafeId: string | null } {
  try {
    const parsed = JSON.parse(localStorage.getItem('cart') || 'null');
    return {
      items: Array.isArray(parsed?.items) ? parsed.items : [],
      cafeId: parsed?.cafeId || null,
    };
  } catch {
    return { items: [], cafeId: null };
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const savedCart = readSavedCart();
  const [items, setItems] = useState<CartItem[]>(savedCart.items);
  const [cafeId, setCafeId] = useState<string | null>(savedCart.cafeId);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify({ items, cafeId }));
  }, [items, cafeId]);

  const addItem = (item: CartItem, newCafeId: string) => {
    if (cafeId && cafeId !== newCafeId) {
      // Auto-clear cart when switching cafes (user can re-add if needed)
      setItems([item]);
      setCafeId(newCafeId);
    } else {
      setCafeId(newCafeId);
      setItems(prev => {
        // Treat items with same ID + note + customizations as the same "recipe"
        const existing = prev.find(i => {
          const sameId = i.menu_item_id === item.menu_item_id;
          const sameNote = i.note === item.note;
          const existingCustom = i.customizations || {};
          const incomingCustom = item.customizations || {};
          const sameCustomizations =
            JSON.stringify(existingCustom) === JSON.stringify(incomingCustom);
          return sameId && sameNote && sameCustomizations;
        });

        if (existing) {
          return prev.map(i =>
            i === existing
              ? { ...i, quantity: i.quantity + item.quantity }
              : i
          );
        }

        return [...prev, item];
      });
    }
    trackFunnelEvent('cart_add', newCafeId);
  };

  const removeItem = (menuItemId: string) => {
    setItems(prev => prev.filter(i => i.menu_item_id !== menuItemId));
    if (items.length === 1) {
      setCafeId(null);
    }
  };

  const updateQuantity = (menuItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(menuItemId);
    } else {
      setItems(prev =>
        prev.map(i =>
          i.menu_item_id === menuItemId ? { ...i, quantity } : i
        )
      );
    }
  };

  const clearCart = () => {
    setItems([]);
    setCafeId(null);
  };

  const getTotal = () => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{ items, cafeId, addItem, removeItem, updateQuantity, clearCart, getTotal }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
